// Component: DsShowcase.jsx
// Purpose: Dev-only showcase for the DeeTrade Design System primitives (Phase 2).
//          Renders every primitive × variant in BOTH themes side-by-side (each
//          panel forces its own [data-theme]) so we can visually verify the
//          components match the DS preview cards. Reachable at /ds in dev only.

import React from "react";
import { Button, Card, Badge, GlassPanel, DataTable, PageHeader, Input, Select, Textarea, Toggle, Checkbox, Modal, Tooltip, EmptyState, Icon, ICON_NAMES, BrandReveal, Segmented, Tabs, Toolbar, useToast } from "../../components/ds";
//--> The real primary monogram (mark A), same asset the Sidebar/LandingPage use.
import brandMark from "../../data/logo-icon-light.png";

/*>>> SECTION HELPERS <<<*/
const Group = ({ title, sub, children }) => (
  <section style={{ marginBottom: 28 }}>
    <div className="ds-eyebrow" style={{ marginBottom: 4 }}>{title}</div>
    {sub && (
      <div className="ds-body-sm" style={{ marginBottom: 12, color: "var(--fg-2)" }}>{sub}</div>
    )}
    <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
      {children}
    </div>
  </section>
);

const Stack = ({ children, gap = 12 }) => (
  <div style={{ display: "flex", flexDirection: "column", gap }}>{children}</div>
);

/*>>> FORM INPUTS demo — stateful (Toggle/Checkbox are controlled) <<<*/
const FormDemo = () => {
  const [live, setLive] = React.useState(true);
  const [autoSq, setAutoSq] = React.useState(true);
  const [notif, setNotif] = React.useState(false);
  return (
    <Stack gap={12}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, width: "100%" }}>
        <Input label="Instrument" defaultValue="NIFTY 50" />
        <Input label="Quantity (lots)" defaultValue="2" hint="1 lot = 50 units" />
        <Select label="Order type"><option>Market</option><option>Limit</option><option>Stop</option></Select>
        <Input label="Limit price" prefix="₹" defaultValue="19,284.35" />
        <Input label="Stop loss" suffix="%" defaultValue="2.5" />
        <Input label="API endpoint" defaultValue="invalid-token" error="Session token expired — re-authenticate." />
      </div>
      <Textarea label="Notes" placeholder="Strategy description…" />
      <div style={{ display: "flex", gap: 18, alignItems: "center", flexWrap: "wrap", paddingTop: 12, borderTop: "1px solid var(--divider)" }}>
        <Toggle checked={live} onChange={setLive} label="Live trading" />
        <Checkbox checked={autoSq} onChange={setAutoSq} label="Auto-square-off at 3:20" />
        <Checkbox checked={notif} onChange={setNotif} label="Notifications" />
      </div>
    </Stack>
  );
};

/*>>> SEGMENTED demo — stateful single-select facet filters (the Watchlist
     country + instrument-type filters, plus an icon variant). Shows the DS
     decision: the active option is a CALM raised chip (subtle --bg-3 surface +
     soft shadow), NOT a loud near-white fill. See the Segmented group note. <<<*/
const SegmentedDemo = () => {
  const [country, setCountry] = React.useState("ALL");
  const [type, setType] = React.useState("ALL");
  const [view, setView] = React.useState("card");
  return (
    <Stack gap={14}>
      <Segmented
        aria-label="Country filter"
        value={country}
        onChange={setCountry}
        options={[
          { value: "ALL", label: "All Market" },
          { value: "IN", label: "India" },
          { value: "US", label: "USA" },
          { value: "JP", label: "Japan" },
        ]}
      />
      <Segmented
        aria-label="Instrument type"
        value={type}
        onChange={setType}
        options={[
          { value: "ALL", label: "All" },
          { value: "EQUITY", label: "Equity" },
          { value: "FUT", label: "Futures" },
          { value: "OPT", label: "Options" },
        ]}
      />
      <Segmented
        aria-label="View mode"
        value={view}
        onChange={setView}
        options={[
          { value: "card", label: "Cards", icon: <Icon name="grid" size={12} /> },
          { value: "compact", label: "Compact", icon: <Icon name="list" size={12} /> },
        ]}
      />
    </Stack>
  );
};

/*>>> TABS demo — stateful (underline section-nav + pill view-toggles) <<<*/
const TabsDemo = () => {
  const [uTab, setUTab] = React.useState(1);
  const [pTab, setPTab] = React.useState("chart");
  return (
    <Stack gap={16}>
      <div style={{ width: "100%" }}>
        <div className="ds-eyebrow" style={{ marginBottom: 6, color: "var(--fg-3)" }}>underline · section nav</div>
        <Tabs value={uTab} onChange={setUTab} tabs={[
          { label: "Overview" },
          { label: "Orders", badge: 12 },
          { label: "Positions", badge: 3 },
          { label: "Activity", icon: <Icon name="search" size={14} /> },
        ]} />
      </div>
      <div style={{ width: "100%" }}>
        <div className="ds-eyebrow" style={{ marginBottom: 6, color: "var(--fg-3)" }}>pill · 2–4 view toggles</div>
        <Tabs variant="pill" value={pTab} onChange={setPTab} tabs={[
          { value: "chart", label: "Chart" },
          { value: "depth", label: "Depth" },
          { value: "algo", label: "Algo" },
        ]} />
      </div>
    </Stack>
  );
};

/*>>> TOOLBAR demo — Tier-2 controls row: Tabs (nav) + Segmented (filters) + accent CTA <<<*/
const ToolbarDemo = () => {
  const [tab, setTab] = React.useState(0);
  const [view, setView] = React.useState("all");
  return (
    <div style={{ width: "100%" }}>
      <Toolbar
        nav={<Tabs value={tab} onChange={setTab} tabs={[
          { label: "All", badge: 24 }, { label: "Equity", badge: 12 }, { label: "F&O", badge: 8 },
        ]} />}
        filters={<Segmented aria-label="Mode" value={view} onChange={setView} options={[
          { value: "all", label: "All" }, { value: "live", label: "Live" }, { value: "paper", label: "Paper" },
        ]} />}
        actions={<Button variant="accent" size="md">+ Add instrument</Button>}
      />
    </div>
  );
};

/*>>> SAMPLE ORDER ROWS for the DataTable demo <<<*/
const ORDERS = [
  { t: "09:17:04", sym: "NIFTY 19300 CE", note: "24-Apr · weekly", side: "Buy", qty: 50, px: "142.35", pnl: "+₹2,450", up: true, status: "Filled", sv: "profit" },
  { t: "09:32:18", sym: "BANKNIFTY 44800 PE", note: "24-Apr · weekly", side: "Sell", qty: 25, px: "318.75", pnl: "+₹8,125", up: true, status: "Filled", sv: "profit", selected: true },
  { t: "10:04:51", sym: "RELIANCE", note: "Reliance Industries · EQ", side: "Buy", qty: 100, px: "2,418.40", pnl: "−₹1,220", up: false, status: "Working", sv: "warn" },
  { t: "10:44:07", sym: "NIFTY 19400 CE", note: "24-Apr · weekly", side: "Buy", qty: 75, px: "—", pnl: "—", up: null, status: "Rejected", sv: "loss" },
];

/*>>> BRAND REVEAL — faithful reproduction of logos.html (stage + cold-start +
     first sign-in). Monogram is white on dark (icon-white filter), black on the
     light card. Looped so the rings + glow stay visible. <<<*/
const WHITE_MARK = { display: "block", filter: "brightness(0) invert(1)" };
const BLACK_MARK = { display: "block", filter: "brightness(0)" };
const mark = (px, style) => <img src={brandMark} alt="DeeTrade" width={px} height={px} style={style} />;

const BrandRevealDemo = () => (
  <div style={{ width: "100%" }}>
    {/* Reveal stage — dark, white monogram + white rings, looping */}
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "52px 28px", borderRadius: 12, color: "#f5f5f5", background: "radial-gradient(circle at 50% 45%, #1f1f1f 0%, #0d0d0d 70%)" }}>
      <BrandReveal size={120} loop icon={mark(96, WHITE_MARK)} />
    </div>
    {/* Context mocks */}
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 24, marginTop: 20, justifyItems: "center" }}>
      {/* cold-start splash (phone) */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
        <div style={{ width: 200, height: 300, borderRadius: 24, border: "8px solid #262626", background: "#0d0d0d", color: "#f5f5f5", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16 }}>
          <BrandReveal size={88} loop icon={mark(64, WHITE_MARK)} />
          <div style={{ fontFamily: "var(--font-display)", fontSize: 20, fontWeight: 500, letterSpacing: "-0.02em" }}>deetrade</div>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "#8A93A3", letterSpacing: ".04em" }}>trade smart.</div>
        </div>
        <div className="ds-eyebrow">cold-start splash</div>
        <div style={{ fontSize: 12, color: "var(--fg-2)", textAlign: "center", maxWidth: "30ch", lineHeight: 1.5 }}>Plays once on app launch before the home view loads — a 1.6s brand beat.</div>
      </div>
      {/* first sign-in (welcome card) */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
        <div style={{ width: 240, background: "#fff", border: "1px solid #E6E0D4", borderRadius: 14, padding: "28px 20px", display: "flex", flexDirection: "column", alignItems: "center", gap: 14, color: "#1a1a1a" }}>
          <BrandReveal size={72} loop icon={mark(56, BLACK_MARK)} />
          <div style={{ fontFamily: "var(--font-display)", fontSize: 18, fontWeight: 500, letterSpacing: "-0.015em", color: "#1a1a1a" }}>Welcome, Lakshay</div>
          <div style={{ fontSize: 12, color: "#4A5261", textAlign: "center", lineHeight: 1.5 }}>Setting up your portfolio…</div>
        </div>
        <div className="ds-eyebrow">first sign-in</div>
        <div style={{ fontSize: 12, color: "var(--fg-2)", textAlign: "center", maxWidth: "30ch", lineHeight: 1.5 }}>After auth on a new device — the monogram stamps in to mark the moment.</div>
      </div>
    </div>
  </div>
);

/*>>> THE PRIMITIVE SET (rendered once per theme) <<<*/
const Primitives = () => (
  <>
    {/* BUTTONS */}
    <Group title="Buttons · variants" sub="Monochrome primary; accent + buy/sell reserved for commit actions.">
      <Button variant="primary">Primary</Button>
      <Button variant="secondary">Secondary</Button>
      <Button variant="ghost">Ghost</Button>
      <Button variant="danger">Danger</Button>
      <Button variant="accent">Accent</Button>
      <Button variant="commit-buy">Buy</Button>
      <Button variant="commit-sell">Sell</Button>
      <Button variant="primary" disabled>Disabled</Button>
    </Group>
    <Group title="Buttons · sizes" sub="sm · md · lg, plus icon-only.">
      <Button size="sm">Small</Button>
      <Button size="md">Medium</Button>
      <Button size="lg">Large</Button>
      <Button iconOnly aria-label="settings">⚙</Button>
      <Button as="a" href="#ds" variant="secondary">As link</Button>
    </Group>

    {/* BADGES */}
    <Group title="Badges · status" sub="Tinted pill + optional dot/pulse. Sentence-case, never a solid fill.">
      <Badge variant="profit" dot pulse>Live</Badge>
      <Badge variant="info" dot>Paper</Badge>
      <Badge variant="warn" dot pulse>Connecting</Badge>
      <Badge variant="neutral" dot>Offline</Badge>
      <Badge variant="loss" dot>Failed</Badge>
    </Group>
    <Group title="Badges · order / role / shapes">
      <Badge variant="profit">Filled</Badge>
      <Badge variant="warn">Pending</Badge>
      <Badge variant="loss">Rejected</Badge>
      <Badge variant="role">Admin</Badge>
      <Badge variant="accent">Trader</Badge>
      <Badge variant="profit" tone="outline">Outline</Badge>
      <Badge variant="neutral" square mono>BTCUSD</Badge>
      <Badge variant="profit" mono>+₹12,450 · +2.34%</Badge>
      <Badge variant="neutral" size="xs">xs</Badge>
      <Badge variant="neutral" size="lg">lg</Badge>
    </Group>

    {/* CARDS */}
    <Group title="Card · base surface" sub="The reusable primitive — header / body / footer slots, optional hover lift.">
      <Card style={{ width: 220 }}>
        <Card.Header>
          <div className="ds-h3">Portfolio</div>
        </Card.Header>
        <Card.Body>
          <div className="ds-mono" style={{ fontSize: 26, letterSpacing: "-0.02em" }}>₹2.41L</div>
          <div className="ds-body-sm" style={{ marginTop: 4 }}>
            <span style={{ color: "var(--success)" }}>+₹9,355</span> today
          </div>
        </Card.Body>
        <Card.Footer>
          <Button size="sm" variant="ghost">Details</Button>
        </Card.Footer>
      </Card>
      <Card hover style={{ width: 220 }}>
        <Card.Body>
          <div className="ds-eyebrow" style={{ marginBottom: 6 }}>Hover me</div>
          <div className="ds-body">Lifts on hover via <code>.card-hover</code>.</div>
        </Card.Body>
      </Card>
    </Group>

    {/* GLASS PANEL */}
    <Group title="GlassPanel" sub="Frosted translucent surface (reads over imagery / color).">
      <GlassPanel style={{ padding: 20, borderRadius: "var(--radius-lg)", width: 300 }}>
        <div className="ds-h3" style={{ marginBottom: 4 }}>Frosted glass</div>
        <div className="ds-body-sm">backdrop-blur + translucent fill, theme-aware.</div>
      </GlassPanel>
    </Group>

    {/* DATA TABLE */}
    <Group title="DataTable" sub="Sticky hairline header, no zebra, row hover/selected, tabular-nums.">
      <Card pad="none" style={{ width: "100%", overflow: "hidden" }}>
        <DataTable>
          <DataTable.Head>
            <tr>
              <DataTable.HeaderCell>Time</DataTable.HeaderCell>
              <DataTable.HeaderCell>Symbol</DataTable.HeaderCell>
              <DataTable.HeaderCell>Side</DataTable.HeaderCell>
              <DataTable.HeaderCell numeric>Qty</DataTable.HeaderCell>
              <DataTable.HeaderCell numeric sortable sortDir="asc">Price</DataTable.HeaderCell>
              <DataTable.HeaderCell numeric>P&amp;L</DataTable.HeaderCell>
              <DataTable.HeaderCell>Status</DataTable.HeaderCell>
            </tr>
          </DataTable.Head>
          <DataTable.Body>
            {ORDERS.map((o) => (
              <DataTable.Row key={o.t} active={o.selected}>
                <DataTable.Cell muted className="ds-mono">{o.t}</DataTable.Cell>
                <DataTable.Cell>
                  <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.25 }}>
                    <span className="ds-mono" style={{ fontSize: 11.5 }}>{o.sym}</span>
                    <span style={{ fontSize: 10, color: "var(--fg-2)" }}>{o.note}</span>
                  </div>
                </DataTable.Cell>
                <DataTable.Cell>
                  <Badge variant={o.side === "Buy" ? "profit" : "loss"} square size="sm">{o.side}</Badge>
                </DataTable.Cell>
                <DataTable.Cell numeric>{o.qty}</DataTable.Cell>
                <DataTable.Cell numeric muted={o.px === "—"}>{o.px}</DataTable.Cell>
                <DataTable.Cell numeric muted={o.pnl === "—"}
                  style={o.up === null ? undefined : { color: o.up ? "var(--success)" : "var(--danger)" }}>
                  {o.pnl}
                </DataTable.Cell>
                <DataTable.Cell>
                  <Badge variant={o.sv} dot>{o.status}</Badge>
                </DataTable.Cell>
              </DataTable.Row>
            ))}
          </DataTable.Body>
        </DataTable>
      </Card>
    </Group>

    {/* SEGMENTED */}
    <Group title="Segmented" sub="Single-select facet filter for VIEW-scoped controls (Toolbar / page header). Recessed group + a sliding thumb that glides between options. DECISION: the active option is a calm raised chip — subtle --bg-3 surface + soft shadow in both themes — which replaced the earlier loud near-white (--fg-1) fill. Optional leading icon.">
      <div style={{ width: "100%" }}><SegmentedDemo /></div>
    </Group>

    {/* TABS */}
    <Group title="Tabs" sub="Section navigation. 'underline' (default) — understated label + one accent indicator that glides between tabs; supports badge counts + a leading icon; for detail-page sections. 'pill' — soft accent-tinted pills, ONLY for 2–4 view/filter toggles.">
      <div style={{ width: "100%" }}><TabsDemo /></div>
    </Group>

    {/* TOOLBAR */}
    <Group title="Toolbar" sub="Tier-2 'Controls' row (flat, bordered-bottom): page Tabs on the left (nav) + a right cluster of VIEW-scoped Segmented filters and the single accent CTA (actions). The right cluster wraps as one unit on narrow screens; page-GLOBAL filters go in the PageHeader instead.">
      <div style={{ width: "100%" }}><ToolbarDemo /></div>
    </Group>

    {/* FORM INPUTS */}
    <Group title="Form inputs" sub="Sentence-case labels · 13px · 6px radius · sm28/md32/lg40 (match buttons) · shared focus ring · affix · toggle · checkbox.">
      <div style={{ width: "100%" }}><FormDemo /></div>
    </Group>

    {/* TOOLTIP */}
    <Group title="Tooltip" sub="Inverted bubble on hover/focus · placement top/bottom/left/right.">
      <Tooltip content="Top tooltip" placement="top"><Button size="sm" variant="secondary">Top</Button></Tooltip>
      <Tooltip content="Bottom tooltip" placement="bottom"><Button size="sm" variant="secondary">Bottom</Button></Tooltip>
      <Tooltip content="Left tooltip" placement="left"><Button size="sm" variant="secondary">Left</Button></Tooltip>
      <Tooltip content="Right tooltip" placement="right"><Button size="sm" variant="secondary">Right</Button></Tooltip>
      <Tooltip content="Refresh session"><Button size="sm" iconOnly aria-label="refresh">↻</Button></Tooltip>
    </Group>

    {/* EMPTY STATE */}
    <Group title="EmptyState" sub="Centered icon + title + description + optional action, for empty lists/panels.">
      <Card style={{ width: "100%" }}>
        <EmptyState
          icon={<Icon name="portfolio" size={40} strokeWidth={1.5} />}
          title="No trade accounts yet"
          description="Connect your first broker account to start monitoring positions and running strategies."
          action={<Button size="sm">Add account</Button>}
        />
      </Card>
    </Group>

    {/* ICONS */}
    <Group title="Icons" sub="Lucide family · 1.8 stroke · currentColor (color it with text-*) · size to match text.">
      <div style={{ display: "grid", gridTemplateColumns: "repeat(8, 1fr)", gap: 8, width: "100%" }}>
        {ICON_NAMES.map((n) => (
          <div key={n} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, padding: "10px 4px", border: "1px solid var(--divider)", borderRadius: 8, color: "var(--fg-1)" }}>
            <Icon name={n} size={20} />
            <span style={{ fontSize: 9.5, color: "var(--fg-2)", fontFamily: "var(--font-mono)" }}>{n}</span>
          </div>
        ))}
      </div>
    </Group>
    <Group title="Icon · sizes & semantic" sub="14 / 16 / 20 / 24 / 32 — match the text. Semantic tint via text color.">
      <span style={{ color: "var(--fg-1)", display: "inline-flex", alignItems: "flex-end", gap: 14 }}>
        <Icon name="search" size={14} /><Icon name="search" size={16} /><Icon name="search" size={20} /><Icon name="search" size={24} /><Icon name="search" size={32} />
      </span>
      <span className="text-profit"><Icon name="gain" size={22} /></span>
      <span className="text-loss"><Icon name="loss" size={22} /></span>
      <span className="text-warning"><Icon name="warn" size={22} /></span>
      <span className="text-accent"><Icon name="notify" size={22} /></span>
    </Group>

    {/* BRAND REVEAL */}
    <Group title="BrandReveal" sub="Monogram stamps in with rings + glow, once — for cold-start / first sign-in. Replay remounts it.">
      <BrandRevealDemo />
    </Group>
  </>
);

/*>>> ONE THEME PANEL <<<*/
const ThemePanel = ({ theme }) => (
  <div
    data-theme={theme}
    style={{
      background: "var(--bg-1)",
      color: "var(--fg-1)",
      padding: "24px 22px 32px",
      borderRadius: 14,
      border: "1px solid var(--border)",
      minWidth: 0,
    }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 20 }}>
      <span className="ds-h2">{theme === "dark" ? "Dark" : "Light"}</span>
      <Badge variant="neutral" size="xs" mono>data-theme="{theme}"</Badge>
    </div>
    <Primitives />
  </div>
);

/*>>> PAGE HEADER SAMPLE — a framed mock "page" to show the identity slot in place <<<*/
const Tile = ({ h = 64, soft = false }) => (
  <div style={{ height: h, borderRadius: 8, background: soft ? "var(--bg-3)" : "var(--bg-2)", border: "1px solid var(--divider)" }} />
);
const BODIES = {
  cards: <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>{[0, 1, 2].map((i) => <Tile key={i} />)}</div>,
  kpis: <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>{[0, 1, 2, 3].map((i) => <Tile key={i} h={56} />)}</div>,
  rows: <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>{[0, 1, 2, 3].map((i) => <Tile key={i} h={28} soft />)}</div>,
};

const PageFrame = ({ theme, body = "cards", ...header }) => (
  <div data-theme={theme} style={{ background: "var(--bg-1)", color: "var(--fg-1)", border: "1px solid var(--border)", borderRadius: 12, overflow: "hidden" }}>
    {/* no top padding here — the 28px top space comes from .ds-page-header, proving it's identical per page */}
    <div style={{ padding: "0 22px 22px" }}>
      <PageHeader {...header} />
      {BODIES[body]}
    </div>
  </div>
);

/*>>> OVERLAYS demo — Modal + Toast (global, portal to body) <<<*/
const OverlaysDemo = () => {
  const toast = useToast();
  const [open, setOpen] = React.useState(false);
  return (
    <div style={{ marginTop: 44 }}>
      <h2 style={{ color: "#fff", fontFamily: "var(--font-display)", fontSize: 20, fontWeight: 700, letterSpacing: "-0.01em", margin: "0 0 4px" }}>
        Overlays — Modal &amp; Toast
      </h2>
      <p style={{ color: "#8b94a5", fontSize: 13, lineHeight: 1.55, margin: "0 0 18px", maxWidth: "78ch" }}>
        Global primitives — they portal to <code>document.body</code> and use the app's current theme. Modal is focus-trapped + scroll-locked (ESC / backdrop closes). Toasts stack bottom-right, auto-dismiss (hover to pause), badge-colored.
      </p>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <Button onClick={() => setOpen(true)}>Open modal</Button>
        <Button variant="secondary" onClick={() => toast.success("Order placed successfully", { title: "Filled" })}>Toast · success</Button>
        <Button variant="secondary" onClick={() => toast.error("Session token expired", { title: "Error" })}>Toast · error</Button>
        <Button variant="secondary" onClick={() => toast.warning("Available margin is low")}>Toast · warning</Button>
        <Button variant="secondary" onClick={() => toast.info("Market opens in 5 minutes")}>Toast · info</Button>
      </div>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Confirm order"
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="commit-buy" onClick={() => { setOpen(false); toast.success("Order placed successfully", { title: "Filled" }); }}>Place order</Button>
          </>
        }
      >
        <p style={{ margin: 0 }}>Buy <b>50 × NIFTY 19300 CE</b> at market price. Tab cycles within the dialog; ESC or a backdrop click closes it.</p>
      </Modal>
    </div>
  );
};

const DsShowcase = () => (
  <div style={{ minHeight: "100vh", background: "#0d0d0d", padding: "28px 24px", fontFamily: "var(--font-body)" }}>
    <div style={{ maxWidth: 1280, margin: "0 auto" }}>
      <h1 style={{ color: "#fff", fontFamily: "var(--font-display)", fontSize: 26, fontWeight: 800, letterSpacing: "-0.02em", margin: "0 0 4px" }}>
        DeeTrade Design System — Primitives
      </h1>
      <p style={{ color: "#8b94a5", fontSize: 13, margin: "0 0 24px" }}>
        Phase 2 core set · Button · Card · Badge · GlassPanel · DataTable. Both themes render
        from the same components — only the tokens swap.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(420px, 1fr))", gap: 20 }}>
        <ThemePanel theme="light" />
        <ThemePanel theme="dark" />
      </div>

      {/* ===== PAGE HEADER STANDARD (proposed) ===== */}
      <div style={{ marginTop: 44 }}>
        <h2 style={{ color: "#fff", fontFamily: "var(--font-display)", fontSize: 20, fontWeight: 700, letterSpacing: "-0.01em", margin: "0 0 4px" }}>
          Page header — one identity for every page
        </h2>
        <p style={{ color: "#8b94a5", fontSize: 13, lineHeight: 1.55, margin: "0 0 18px", maxWidth: "78ch" }}>
          Uniform standard: every page gets the same treatment in the same spot — fixed top space, a title in
          Inter/800, and one muted description line. No eyebrow and no per-page accent word, so nothing varies
          between pages. Move between the three mock pages below: the heading and description land in exactly the
          same place. Same component, both themes. Title size/weight is tunable — tell me if you want it bigger,
          smaller, or upper-case.
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(440px, 1fr))", gap: 20 }}>
          {["light", "dark"].map((theme) => (
            <div key={theme} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <PageFrame theme={theme} body="cards"
                title="Market Watch" subtitle="Real-time monitoring & algo execution status" />
              <PageFrame theme={theme} body="kpis"
                title="Overview" subtitle="Your portfolio at a glance — P&L, open positions, and algo health." />
              <PageFrame theme={theme} body="rows"
                title="Orders" subtitle="Today's order history across all broker accounts." />
            </div>
          ))}
        </div>
      </div>

      <OverlaysDemo />
    </div>
  </div>
);

export default DsShowcase;
